import cn from 'classnames/bind';
import React, { FunctionComponent, useState, useCallback, useRef, useLayoutEffect } from 'react';

import { getImageUrlBySize } from '@/shared/lib/images';

import { ImageProps } from './Image.types';
import styles from './Image.module.css';

const cx = cn.bind(styles);

const Image: FunctionComponent<ImageProps> = (props) => {
    const {
        imageUrl,
        resolution,
        placeholderResolution,
        customPlaceholder,
        className,
        imageClassName,
        placeholderClassName,
        isAnimationAllowed = true,
        removeScheme = true,
        useBackgroundImage = false,
        style,
        imageStyles,
        onLoaded,
        onPlaceholderLoaded,
        onError,
        alt = '',
        'aria-hidden': ariaHidden,
    } = props;

    const imageRef = useRef<HTMLImageElement | null>(null);

    const placeholderImageUrl = getImageUrlBySize(imageUrl, placeholderResolution, removeScheme);
    const actualImageUrl = resolution
        ? getImageUrlBySize(imageUrl, resolution, removeScheme)
        : imageUrl;

    const [imageLoaded, setImageLoaded] = useState(false);
    const [placeholderVisible, setPlaceholderVisible] = useState(true);

    const isDifferentImages = placeholderImageUrl !== actualImageUrl;
    const isImageVisible = Boolean(customPlaceholder) || isDifferentImages || imageLoaded;

    const onLoadCallback = useCallback(() => {
        setImageLoaded(true);

        if (!isAnimationAllowed) {
            setPlaceholderVisible(false);
        }

        if (onLoaded) {
            onLoaded();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnimationAllowed]);

    const onPlaceholderTransitionEnd = useCallback(() => setPlaceholderVisible(false), []);

    useLayoutEffect(() => {
        const isComplete = Boolean(imageRef.current?.complete);

        setImageLoaded(isComplete);
        setPlaceholderVisible(!isComplete);
    }, [imageUrl]);

    const placeholderClassList = cx(
        'image-placeholder',
        !imageLoaded && 'image-placeholder_active',
        !isDifferentImages && 'image-placeholder_only-image',
        imageClassName,
        placeholderClassName
    );

    const imageClassList = cx('image', imageLoaded && 'image_active', imageClassName);

    if (useBackgroundImage) {
        return (
            <div
                className={cx('root', 'background-image', className)}
                style={{ ...style, backgroundImage: `url(${actualImageUrl})` }}
            />
        );
    }

    const placeholder = customPlaceholder || (
        <img
            className={placeholderClassList}
            style={imageStyles}
            src={placeholderImageUrl}
            alt={alt}
            onLoad={onPlaceholderLoaded}
            onTransitionEnd={isAnimationAllowed ? onPlaceholderTransitionEnd : undefined}
            aria-hidden={ariaHidden}
        />
    );

    return (
        <div className={cx('root', className)} style={style}>
            {placeholderVisible && placeholder}
            {isImageVisible && (
                <img
                    className={imageClassList}
                    style={imageStyles}
                    src={actualImageUrl}
                    alt={alt}
                    onLoad={onLoadCallback}
                    onError={onError}
                    ref={imageRef}
                    aria-hidden={ariaHidden}
                />
            )}
        </div>
    );
};

export default Image;
